export class QuantityAdjuster extends window.BAO.CustomElement() {
  static get observedAttributes () {
    return ['value', 'disabled', 'min', 'max']
  }

  static get styles () {
    return `
      :host { display: block; }
      .qty-Adjuster_Remove, .qty-Adjuster_Control-minus { display: none; } /* Initially hidden */
    `
  }

  constructor () {
    super()

    this.setup()
  }

  connectedCallback () {
    super.connectedCallback()

    if (!this.hasAttribute('value')) {
      this.setAttribute('value', this.min.toString())
    }

    if (!this.hasAttribute('min')) {
      this.setAttribute('value', this.min.toString())
    }

    this._upgradeProperty('value')
    this._upgradeProperty('disabled')

    this.adjustRemoveBtnVisibility() // Adjust visibility on load based on initial values
  }

  attributeChangedCallback (name, oldValue, newValue) {
    const isANumber = isNaN(parseInt(newValue, 10)) === false

    switch (name) {
      case 'value': {
        if (!isANumber) break
        if (parseInt(oldValue, 10) === parseInt(newValue, 10)) break

        this.handleValueChangeSideEffects(parseInt(newValue, 10))

        break
      }
      case 'min': {
        if (!isANumber) break

        if (this.value < parseInt(newValue, 10)) {
          this.value = parseInt(newValue, 10)
        }

        break
      }
      case 'max': {
        if (!isANumber) break

        if (this.value > parseInt(newValue, 10)) {
          this.value = parseInt(newValue, 10)
        }

        break
      }
      case 'disabled': {
        const hasValue = newValue != null

        this.minusButton.disabled = hasValue
        this.plusButton.disabled = hasValue
        this.input.disabled = hasValue
      }
    }

    this.adjustRemoveBtnVisibility() // Ensure we adjust visibility after any attribute change
  }

  adjustRemoveBtnVisibility () {
    // Logic to toggle visibility based on conditions
    const removeBtn = this.querySelector('.qty-Adjuster_Remove')
    const minusBtn = this.querySelector('.qty-Adjuster_Control-minus')

    if (removeBtn) {
      const value = this.value
      const min = this.min

      if (removeBtn && minusBtn) {
        if (value === min) {
          removeBtn.style.display = 'none'
          minusBtn.style.display = 'flex'
        } else {
          removeBtn.style.display = 'flex'
          minusBtn.style.display = 'none'
        }
      }
    }
  }

  setup () {
    this.input.setAttribute('value', this.value.toString())
    this.input.value = this.value.toString()
    this.input.name = this.name

    if (this.value === 1) {
      this.minusButton.disabled = true
    }
  }

  setupListeners () {
    super.setupListeners()

    this.onMinusClick = this.onMinusClick.bind(this)
    this.onPlusClick = this.onPlusClick.bind(this)
    this.onInputChange = this.onInputChange.bind(this)
    this.onInputInput = this.onInputInput.bind(this)

    this.listeners.add(this.minusButton, 'click', this.onMinusClick)
    this.listeners.add(this.plusButton, 'click', this.onPlusClick)
    this.listeners.add(this.input, 'change', this.onInputChange)
    this.listeners.add(this.input, 'input', this.onInputInput)
  }

  onMinusClick () {
    if (this.value === 1) return

    this.value -= 1
  }

  onPlusClick () {
    this.value += 1
  }

  /**
   * @param {Event} e
   *
   * @returns {void}
   */
  onInputChange (e) {
    if (isNaN(e.target.valueAsNumber) === false) {
      this.value = e.target.valueAsNumber
    }
  }

  /**
   * @param {InputEvent} e
   *
   * @returns {void}
   */
  onInputInput (e) {
    if (isNaN(e.target.valueAsNumber) === false) {
      this.value = e.target.valueAsNumber
    }
  }

  /**
   * @param {Number} val
   *
   * @returns {void}
   */
  handleValueChangeSideEffects (val) {
    const oldVal = this.value
    if (oldVal === val) return

    this.input.value = val.toString()
    if (val === this.min) {
      this.minusButton.setAttribute('disabled', 'disabled')
    } else {
      this.minusButton.removeAttribute('disabled')
    }
    this.plusButton.disabled = this.max != null && val === this.max

    this.dispatchEvent(
      new CustomEvent(window.BAO.EVENTS.QUANTITY_ADJUSTER.CHANGE, {
        detail: {
          oldValue: oldVal,
          value: val,
        },
        bubbles: true,
      }),
    )

    this.adjustRemoveBtnVisibility() // Call to adjust visibility based on new value
  }

  /**
   * @param {Number} val
   *
   * @returns {void}
   */
  set value (val) {
    if (val < this.min) {
      val = this.min
    }

    this.handleValueChangeSideEffects(val)

    this.setAttribute('value', val.toString())
  }

  /**
   * @returns {Number}
   */
  get value () {
    return parseInt(this.getAttribute('value'), 10)
  }

  /**
   * @returns {Number}
   */
  get min () {
    return parseInt(this.getAttribute('min'), 10) || 1
  }

  /**
   * @returns {Number|null}
   */
  get max () {
    return parseInt(this.getAttribute('max'), 10) || null
  }

  /**
   * @returns {String}
   */
  get name () {
    return this.getAttribute('name') || 'quantity'
  }

  /**
   * @returns {HTMLButtonElement}
   */
  get minusButton () {
    return this.els.minus.element
  }

  /**
   * @returns {HTMLButtonElement}
   */
  get plusButton () {
    return this.els.plus.element
  }

  /**
   * @returns {HTMLInputElement}
   */
  get input () {
    return this.els.input.element
  }
}

if (!customElements.get('quantity-adjuster')) {
  customElements.define('quantity-adjuster', QuantityAdjuster)
}
